import {
  type AdditionalComparisonColumnType,
  type ComparisonRangeDateRange,
  DEFAULT_DELTA_DISPLAY_MODE,
  type DeltaDisplayMode,
  type DisplayOptionsType,
} from '../reportConfigurationsTypes';

type DeltaDisplayOptions = Partial<Pick<DisplayOptionsType, 'deltaDisplayMode'>>;

/** Resolve the comparison-delta presentation for new and legacy saved reports. */
export const resolveDeltaDisplayMode = (displayOptions: DeltaDisplayOptions | null | undefined): DeltaDisplayMode =>
  displayOptions?.deltaDisplayMode ?? DEFAULT_DELTA_DISPLAY_MODE;

/**
 * The comparison modes this picker exposes.
 *
 * A deliberate subset of `ComparisonRangeDateRange`. The contract also carries `CUSTOM_SINCE`,
 * `CUSTOM_LAST` and `CUSTOM_RECENT` plus every `GeneratedReportRanges` value, none of which the
 * design offers — see SIMPL-35047. Typing the ids as `ComparisonRangeDateRange` keeps them checked
 * against the contract, so a mode that stops being valid fails the build here.
 */
export type ComparisonMode = Extract<ComparisonRangeDateRange, 'PRIOR_YEAR_PERIOD' | 'PRIOR_PERIOD' | 'CUSTOM'>;

export type ComparisonModeOption = {
  id: ComparisonMode;
  label: string;
  /** Renders a divider below this entry, separating the relative modes from Custom. */
  dividerAfter?: boolean;
};

export const COMPARISON_MODES: readonly ComparisonModeOption[] = [
  { id: 'PRIOR_YEAR_PERIOD', label: 'Prior year' },
  { id: 'PRIOR_PERIOD', label: 'Prior period', dividerAfter: true },
  { id: 'CUSTOM', label: 'Custom' },
];

/** Modes whose dates are derived from the primary range rather than typed by the user. */
export const isRelativeComparisonMode = (mode: ComparisonMode): boolean => mode !== 'CUSTOM';

/**
 * Narrow an arbitrary stored `dateRange` to a mode the picker can display, or `undefined` when there
 * is nothing to show.
 *
 * `undefined` is a real state, not a failure: the picker opens on a report with no comparison yet
 * (Figma `Selection=None`), where the rail has no selection and the Compare to row shows a
 * placeholder. It is also the answer for a `dateRange` with no rail entry — a config synced from a
 * client exposing more modes than this one — where highlighting an arbitrary rail entry would
 * misreport what the report actually does.
 */
export const toComparisonMode = (dateRange: ComparisonRangeDateRange | undefined): ComparisonMode | undefined =>
  COMPARISON_MODES.some((mode) => mode.id === dateRange) ? (dateRange as ComparisonMode) : undefined;

export type ComparisonColumnOption = {
  type: AdditionalComparisonColumnType;
  label: string;
};

/**
 * The two derived columns the picker exposes, in the order they are written to
 * `additionalColumnTypes` — which is the order QCS generates the columns in, so it is significant.
 *
 * `CUMULATIVE_VALUE` is deliberately absent: the design offers only the two deltas, and cumulative
 * is out for v1 (SIMPL-35047). It remains valid in the contract, just unexposed.
 *
 * Beside `COMPARISON_MODES` for the same reason that list is here: it is the option set both
 * pickers offer, and two platforms offering different columns for the same shared config is the
 * failure a second copy produces.
 */
export const COMPARISON_COLUMN_OPTIONS: readonly ComparisonColumnOption[] = [
  { type: 'DELTA_AMOUNT', label: 'Difference ($)' },
  { type: 'DELTA_PERCENTAGE', label: 'Difference (%)' },
];
